import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Progress } from '@quadminds/mindkit';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { RiDriveFill, RiLoader4Line, RiUploadCloudFill } from '@remixicon/react';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosProgressEvent } from 'axios';
import { CheckCircle2, XCircle, Link2, X } from 'lucide-react';
import { FC, useState, useRef, useMemo, useEffect, useCallback } from 'react';
import useDrivePicker from 'react-google-drive-picker';
import { CallbackDoc, PickerCallback, PickerConfiguration } from 'react-google-drive-picker/dist/typeDefs';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { twMerge } from 'tailwind-merge';
import useUploadFileMethod from './hooks/useUploadFileMetod';
import { CLIENT_ID, GOOGLE_API_KEY } from '../../../../../config';
import { useGetGoogleAuthCode } from '../../../../../hooks/useGetGoogleCode';
import { useGoogleValidateToken } from '../../../../../hooks/useGoogleValidateToken';
import { useImportDocumentByUri } from '../../../../../hooks/useImportDocumentByUri';
import { useImportGoogleDocument } from '../../../../../hooks/useImportGoogleDocument';
import { useIsKnowledge } from '../../../../../hooks/useIsKnowledge';
import { useIntegrationContext } from '../../../../../providers/Integration/Integration.provider';
import { useKnowledgeContext } from '../../../../../providers/Knowledge/Knowledge.provider';
import { UploadDestination } from '../../../../../types/File/File.types';
import { FileUploadError } from '../../../../../utils/fileError';
import { getFileIcon } from '../../../../../utils/getFileIcon';
import { getFileNameFromUrl } from '../../../../../utils/utils';
import UriListImport from '../UriListImport/UriListimport.component';

// File errors object to handle different file upload errors
const FILE_ERRORS: Record<string, Record<string, string>> = {
    BAD_FILE: {
        title: 'badFileTitle',
        description: 'badFileDescription',
    },
    DUPLICATED_FILE: {
        title: 'duplicateFileTitle',
        description: 'duplicateFileDescription',
    },
    TOO_BIG_FILE: {
        title: 'tooBigFileTitle',
        description: 'tooBigFileDescription',
    },
    BAD_FILE_TYPE: {
        title: 'badFileTypeTitle',
        description: 'badFileTypeDescription',
    },
};

interface UploadFileMethodProps {
    isOpen: boolean;
    onClose: () => void;
    handleDialogState: (state: boolean) => void;
    openGooglePicker?: boolean;
    initialSelectedFiles?: File[];
    dialogs: {
        isRenameDialogOpen: boolean;
        isMoveDialogOpen: boolean;
        isUploadModalOpen: boolean;
        isPreviewModalOpen: boolean;
        isDeleteDialogOpen: boolean;
        isReplaceDialogOpen: boolean;
    };
}

type ApiDetail = { message?: string; code?: string } | string;

const extractApiErrorMessage = (error: unknown): string => {
    const err = error as {
        response?: { data?: { detail?: ApiDetail; message?: string } };
        errCode?: string;
        message?: string;
    };
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === 'object' && 'message' in detail) {
        return detail.message ?? 'Error desconocido';
    }
    return (
        (typeof detail === 'string' ? detail : undefined) ??
        err?.response?.data?.message ??
        err?.errCode ??
        err?.message ??
        'Error desconocido'
    );
};

const UploadFileMethod: FC<UploadFileMethodProps> = ({
    isOpen,
    onClose,
    handleDialogState,
    initialSelectedFiles,
    dialogs,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']); // Retrieve userInfo from the cache
    const currentOrganizationSettings =
        userInfo?.currentOrganization && userInfo.settings
            ? (userInfo.settings[userInfo.currentOrganization] as Record<string, unknown>)
            : undefined;
    const showUploadDestinationSelector = Boolean(currentOrganizationSettings?.['mindy:upload_destination_selector']);
    const [allCompleted, setAllCompleted] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<File[]>(initialSelectedFiles || []);
    const [uploadDestination, setUploadDestination] = useState<UploadDestination>(UploadDestination.STANDARD);
    const [fileValidationErrors, setFileValidationErrors] = useState<Record<string, string>>({});
    const [selectedFromIntegration, setSelectedFromIntegration] = useState<
        (CallbackDoc & { fullfiled?: boolean; error?: string })[]
    >([]);
    const [selectedFilesFromUris, setSelectedFilesFromUris] = useState<
        { uri: string; fullfiled?: boolean; error?: string }[]
    >([]);
    const [isDragginOver, setIsDraggingOver] = useState<boolean>(false);
    const [uriDialogOpen, setUriDialogOpen] = useState<boolean>(false);
    const [integrationEventProgres, setIntegrationEventProgres] = useState<{
        google: Record<string, number>;
        uri: Record<string, number>;
    }>({ google: {}, uri: {} });

    const fileInputRef = useRef<HTMLInputElement>(null);
    const { isKnowledge } = useIsKnowledge();

    const {
        handleIsUploadingFile,
        rejectedUploads,
        statusUploadFiles,
        progressByEvents,
        handleUploadFiles,
        clearUploadStatus,
        clearUploadProgress,
        clearRejectedUploadByFileName,
        isLoadingUploadFile,
    } = useKnowledgeContext();

    useEffect(() => {
        if (!isOpen) {
            setSelectedFiles([]);
            setSelectedFromIntegration([]);
            setSelectedFilesFromUris([]);
            setIntegrationEventProgres({ google: {}, uri: {} });
            setAllCompleted(false);
            setFileValidationErrors({});
            setUploadDestination(UploadDestination.STANDARD);
        }
    }, [isOpen]);

    const { validateSingleFile } = useUploadFileMethod(isKnowledge);

    const overallProgress = useMemo(() => {
        const totalSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);
        const totalProgressFromFiles = selectedFiles.reduce((acc, file) => {
            const fileProgress = progressByEvents?.[file.name] ?? 0;
            return acc + file.size * fileProgress;
        }, 0);

        const totalProgressFromIntegration = selectedFromIntegration.reduce((acc, file) => {
            const fileProgress = integrationEventProgres.google[file.name] ?? 0;
            return acc + fileProgress;
        }, 0);

        const totalProgressFromUris = selectedFilesFromUris.reduce((acc, file) => {
            const fileProgress = integrationEventProgres.uri[file.uri] ?? 0;
            return acc + fileProgress;
        }, 0);

        const totalItems = selectedFiles.length + selectedFromIntegration.length + selectedFilesFromUris.length;

        if (totalItems === 0) return 0;

        return (
            (totalProgressFromFiles + totalProgressFromIntegration + totalProgressFromUris) / (totalSize || totalItems)
        );
    }, [progressByEvents, integrationEventProgres, selectedFiles, selectedFromIntegration, selectedFilesFromUris]);

    const [openPicker] = useDrivePicker();

    const { mutateAsync: getGoogleAuthCode, isPending: isLoadingRedirect } = useGetGoogleAuthCode();
    const { mutateAsync: validateToken } = useGoogleValidateToken();
    const { googleIntegration, setGoogleIntegration } = useIntegrationContext();
    const { mutateAsync: importFromGoogle, isPending: isLoadingGoogleDoc } = useImportGoogleDocument();
    const { mutateAsync: importFromUri, isPending: isLoadingByURL } = useImportDocumentByUri();

    const handleOpenPicker = useCallback(async () => {
        // Check if the Google integration is authenticated
        const tokenInfo = await validateToken({ accessToken: googleIntegration?.accessToken || '' }).catch(() => {
            console.log('Google token validation failed');
        });

        if (tokenInfo && tokenInfo.user_id === undefined) {
            console.error('Invalid Google token or user ID not found');
            setGoogleIntegration({ ...googleIntegration, accessToken: undefined });
            localStorage.removeItem('googleAccessToken');
            return;
        }

        if (googleIntegration.selfOpenedGooglePicker) {
            setGoogleIntegration({ ...googleIntegration, selfOpenedGooglePicker: false });
        } else {
            handleDialogState(false);
        }
        const customView = (globalThis as any).google?.picker?.DocsView
            ? new (globalThis as any).google.picker.DocsView()
                  .setIncludeFolders(false)
                  .setSelectFolderEnabled(false)
                  .setOwnedByMe(true)
                  .setMimeTypes(
                      [
                          'application/pdf', // PDF
                          'application/msword', // .doc (Word 97–2003)
                          'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx (Word)
                          'application/vnd.google-apps.document', // Google Docs
                          'text/plain', // .txt
                      ].join(','),
                  )
            : undefined;
        const config: PickerConfiguration = {
            clientId: CLIENT_ID || '',
            developerKey: GOOGLE_API_KEY || '',
            showUploadView: false,
            showUploadFolders: false,
            viewId: undefined, // Use undefined to show the default view
            token: googleIntegration.accessToken || undefined,
            supportDrives: false,
            multiselect: true,
            disableDefaultView: true,
            customViews: customView && [customView],
            callbackFunction: (data: PickerCallback) => {
                if (data.action === 'picked') {
                    setSelectedFromIntegration((prevData) => [...prevData, ...data.docs]);
                    handleDialogState(true);
                }
                if (data.action === 'cancel') {
                    handleDialogState(true);
                }
            },
        };

        openPicker(config as any);
    }, [googleIntegration, handleDialogState, openPicker, setGoogleIntegration, validateToken]);

    const handleGoogleAuthCode = useCallback(
        async (redirectUrl?: string) => {
            if (googleIntegration?.accessToken === undefined && redirectUrl) {
                getGoogleAuthCode(redirectUrl)
                    .then((response) => {
                        localStorage.setItem('googleOriginPath', '/knowledge');
                        globalThis.location.href = response.data.authorization_url; // Redirect to the Google Auth URL
                    })
                    .catch((error) => {
                        console.error('Error getting Google Auth Code:', error);
                    });
            } else {
                handleOpenPicker();
            }
        },
        [getGoogleAuthCode, googleIntegration, handleOpenPicker],
    );

    const applyFileValidation = (files: File[]) => {
        const errors: Record<string, string> = {};
        files.forEach((file) => {
            const key = `${file.name}|${file.lastModified}`;
            const validationError = validateSingleFile(file);
            if (validationError) errors[key] = validationError;
        });
        setFileValidationErrors((prev) => ({ ...prev, ...errors }));
    };

    const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files && dialogs.isReplaceDialogOpen) {
            const files = Array.from(event.target.files);
            setSelectedFiles(files);
            setFileValidationErrors({});
            applyFileValidation(files);
        } else if (event.target.files) {
            const newFiles = Array.from(event.target.files);
            setSelectedFiles([...selectedFiles, ...newFiles]);
            applyFileValidation(newFiles);
        }
        setAllCompleted(false);
        // Reset input value so the same file can be selected again after being removed
        event.target.value = '';
    };

    const handleDragAndDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.dataTransfer.files) {
            const files = Array.from(event.dataTransfer.files);
            setSelectedFiles(files);
            setFileValidationErrors({});
            applyFileValidation(files);
        }
        setAllCompleted(false);
        setIsDraggingOver(false);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!isDragginOver) setIsDraggingOver(true);
    };
    const handleDragExit = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (isDragginOver) setIsDraggingOver(false);
    };

    const openFileSelector = () => {
        fileInputRef.current?.click();
    };

    const removeFileFromDevice = (fileKey: string) => {
        const [name] = fileKey.split('|');
        clearRejectedUploadByFileName(name);
        setAllCompleted(false);
        setFileValidationErrors((prev) => {
            const next = { ...prev };
            delete next[fileKey];
            return next;
        });
        setSelectedFiles((prev) => {
            const [, lastModifiedStr] = fileKey.split('|');
            const lastModified = lastModifiedStr ? Number(lastModifiedStr) : undefined;
            let removed = false;
            return prev.filter((file) => {
                if (removed) return true;
                if (file.name !== name) {
                    return true;
                }
                if (lastModified !== undefined && file.lastModified !== lastModified) {
                    return true;
                }
                removed = true;
                return false;
            });
        });
    };

    const removeFileFromIntegration = (fileId: string) => {
        setAllCompleted(false);
        setSelectedFromIntegration((prev) => prev.filter((file) => file.id !== fileId));
    };

    const removeFileFromUri = (uri: string) => {
        setAllCompleted(false);
        setSelectedFilesFromUris((prev) => prev.filter((file) => file.uri !== uri));
    };

    const handleUploadGoogleIntegration = useCallback((event: AxiosProgressEvent & { name: string }) => {
        const percentage = Math.floor((event.progress || 0) * 100);
        const newValue = { [event.name]: percentage };
        setIntegrationEventProgres((prevState) => ({ ...prevState, google: { ...prevState.google, ...newValue } }));
    }, []);

    const handleUploadUriIntegration = useCallback((event: AxiosProgressEvent & { name: string }) => {
        const percentage = Math.floor((event.progress || 0) * 100);
        const newValue = { [event.name]: percentage };
        setIntegrationEventProgres((prevState) => ({ ...prevState, uri: { ...prevState.uri, ...newValue } }));
    }, []);

    const intregrationCatch = (error: unknown, file: CallbackDoc) => {
        const message = extractApiErrorMessage(error);
        setSelectedFromIntegration((prevState) =>
            prevState.map((f) => (f.id === file.id ? { ...f, error: message } : f)),
        );
    };

    const handleFileIntegration = useCallback(async () => {
        handleIsUploadingFile(true);
        try {
            const googlePromises = selectedFromIntegration.map((file) => {
                const uriFolderId = globalThis.location.pathname.split('/').reverse()[0];
                const parentId = uriFolderId === userInfo?.currentOrganization ? null : uriFolderId;
                return importFromGoogle({
                    fileId: file.id,
                    parentId: parentId,
                    fileName: file.name,
                    externalId: userInfo?.externalId || '',
                    handleProgressEvent: handleUploadGoogleIntegration,
                })
                    .then((response) => {
                        queryClient.invalidateQueries({ queryKey: ['documents'] });
                        return response;
                    })
                    .catch((error: FileUploadError) => {
                        intregrationCatch(error, file);
                        console.error('Error importing from Google:', error);
                    });
            });

            const resolvedPromised = await Promise.allSettled(googlePromises);
            const promisesFullfiled = resolvedPromised
                .filter((result) => result.status === 'fulfilled')
                .map((result) => {
                    const fulfilledResult = result as PromiseFulfilledResult<any>;
                    return fulfilledResult.value;
                });

            setSelectedFromIntegration((prevState) => {
                return prevState.map((file) => ({
                    ...file,
                    fullfiled: promisesFullfiled.some((res) => res?.file_name === file?.name),
                }));
            });
        } finally {
            handleIsUploadingFile(false);
        }
    }, [
        importFromGoogle,
        selectedFromIntegration,
        userInfo?.currentOrganization,
        userInfo?.externalId,
        handleUploadGoogleIntegration,
        queryClient,
        setSelectedFromIntegration,
        handleIsUploadingFile,
    ]);

    const handleFileByUris = useCallback(async (): Promise<void> => {
        handleIsUploadingFile(true);
        try {
            const uriFolderId = globalThis.location.pathname.split('/').reverse()[0];
            const parentId = uriFolderId === userInfo?.currentOrganization ? null : uriFolderId;
            const uriPromises = selectedFilesFromUris.map((data) =>
                importFromUri({
                    uri: data.uri,
                    externalId: userInfo?.externalId || '',
                    parentId: parentId,
                    handleProgressEvent: handleUploadUriIntegration,
                })
                    .then((response) => {
                        queryClient.invalidateQueries({ queryKey: ['documents'] });
                        return response;
                    })
                    .catch((error: unknown) => {
                        const message = extractApiErrorMessage(error);
                        setSelectedFilesFromUris((prevState) =>
                            prevState.map((f) => (f.uri === data.uri ? { ...f, error: message } : f)),
                        );
                        console.error('Error importing from URI:', error);
                    }),
            );

            const uriPromisesResolved = await Promise.allSettled(uriPromises);
            const uriPromisesFullfiled = uriPromisesResolved
                .filter((result) => result.status === 'fulfilled')
                .map((result) => (result as PromiseFulfilledResult<any>).value);
            setSelectedFilesFromUris((prevState) => {
                return prevState.map((file) => ({
                    ...file,
                    fullfiled: uriPromisesFullfiled.some((res) => {
                        if (!res?.file_name) return false;
                        if (res.file_name.includes('document_from_')) {
                            return getFileNameFromUrl(file.uri) === res.file_name;
                        } else {
                            return file.uri.includes(res?.file_name);
                        }
                    }),
                }));
            });
        } finally {
            handleIsUploadingFile(false);
        }
    }, [
        importFromUri,
        selectedFilesFromUris,
        userInfo?.currentOrganization,
        userInfo?.externalId,
        handleUploadUriIntegration,
        queryClient,
        handleIsUploadingFile,
    ]);

    const handleUpload = useCallback(() => {
        // Only upload files that passed front-end validation
        const validFiles = selectedFiles.filter((file) => {
            const key = `${file.name}|${file.lastModified}`;
            return !fileValidationErrors[key];
        });

        // Descarga archivos desde la pc
        if (validFiles.length > 0) {
            handleUploadFiles(validFiles, uploadDestination);
        }

        // descarga desde google
        if (selectedFromIntegration.length > 0) {
            handleFileIntegration();
        }

        // descargar desde uri
        if (selectedFilesFromUris.length > 0) {
            handleFileByUris();
        }
    }, [
        handleUploadFiles,
        selectedFiles,
        fileValidationErrors,
        selectedFilesFromUris,
        selectedFromIntegration,
        handleFileByUris,
        handleFileIntegration,
        uploadDestination,
    ]);

    const handleCloseModal = useCallback(() => {
        setSelectedFiles([]);
        setSelectedFromIntegration([]);
        setSelectedFilesFromUris([]);
        setIntegrationEventProgres({ google: {}, uri: {} });
        setAllCompleted(false);
        setFileValidationErrors({});
        clearUploadProgress();
        clearUploadStatus();
        onClose();
    }, [onClose, clearUploadProgress, clearUploadStatus]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined;
        if (overallProgress >= 100 && isOpen && !isLoadingUploadFile && !isLoadingGoogleDoc && !isLoadingByURL) {
            const isAllFilesSuccessfull =
                statusUploadFiles !== null && Object.values(statusUploadFiles).every((item) => item === 'successful');

            const hasErrors =
                (statusUploadFiles !== null && Object.values(statusUploadFiles).some((item) => item === 'failed')) ||
                selectedFromIntegration.some((f) => f.error) ||
                selectedFilesFromUris.some((f) => f.error);

            setAllCompleted(true);

            if (!hasErrors) {
                timeoutId = setTimeout(
                    () => {
                        handleCloseModal();
                    },
                    isAllFilesSuccessfull ? 1000 : 5000,
                );
            }
        }

        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [
        allCompleted,
        isLoadingByURL,
        isLoadingGoogleDoc,
        isLoadingUploadFile,
        isOpen,
        handleCloseModal,
        overallProgress,
        statusUploadFiles,
        selectedFromIntegration,
        selectedFilesFromUris,
    ]);

    useEffect(() => {
        // If initialSelectedFiles is provided and selectedFiles is empty, set selectedFiles to initialSelectedFiles
        if (initialSelectedFiles && initialSelectedFiles.length > 0 && selectedFiles.length === 0) {
            setSelectedFiles(initialSelectedFiles);
        }
    }, [initialSelectedFiles, selectedFiles.length]);

    const disabledByNothingSelected =
        selectedFiles.length === 0 && selectedFilesFromUris.length === 0 && selectedFromIntegration.length === 0;

    const hasValidationErrors = Object.keys(fileValidationErrors).length > 0;

    const isUploading = isLoadingUploadFile || isLoadingGoogleDoc || isLoadingByURL;

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleCloseModal}>
                <DialogContent className="max-w-[784px] w-full dark:bg-gray-qm-900">
                    <DialogHeader className="mb-2">
                        <DialogTitle className="font-poppins text-xl">
                            {dialogs.isUploadModalOpen
                                ? t('modals.uploadFileMethod.title')
                                : t('modals.uploadFileMethod.replaceTitle')}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="w-full flex justify-center gap-4">
                        <button
                            className={twMerge(
                                'w-1/2 h-[94px] relative rounded-md border border-dashed bg-[#5417EE0A] dark:bg-[#37474F] border-[#B998FA] dark:border-[#78909C] flex flex-col justify-center items-center p-[18px]',
                                isLoadingRedirect && 'animate-pulse',
                            )}
                            onClick={() => handleGoogleAuthCode(globalThis.location.origin + '/apps/mindy-app/auth')}
                            disabled={isLoadingRedirect}
                        >
                            <RiDriveFill className="h-[30px] w-[30px] text-[#8048F7]" />
                            <p className="font-poppins font-medium text-sm">{t('modals.uploadFileMethod.fromDrive')}</p>
                            {googleIntegration.selfOpenedGooglePicker && (
                                <p className="absolute top-2 right-2 px-2 py-1 bg-primary rounded-full text-white text-[10px] font-poppins">
                                    Seleccione documentos
                                </p>
                            )}
                        </button>
                        <button
                            className="w-1/2 h-[94px] relative rounded-md border border-dashed bg-[#5417EE0A] dark:bg-[#37474F] border-[#B998FA] dark:border-[#78909C] flex flex-col justify-center items-center p-[18px]"
                            onClick={() => setUriDialogOpen(true)}
                        >
                            <Link2 className="h-[30px] w-[30px] text-[#8048F7] -rotate-45" />
                            <p className="font-poppins font-medium text-sm">{t('modals.uploadFileMethod.fromUrl')}</p>
                        </button>
                    </div>
                    {showUploadDestinationSelector && dialogs.isUploadModalOpen && (
                        <div className="flex flex-col gap-2 rounded-md border border-dashed border-[#B998FA] dark:border-[#78909C] bg-[#5417EE0A] dark:bg-[#37474F] px-4 py-3">
                            <p className="font-poppins text-sm font-medium">Destino de la subida</p>
                            <select
                                value={uploadDestination}
                                onChange={(e) => setUploadDestination(e.target.value as UploadDestination)}
                                className="h-10 rounded-md border border-input bg-background px-3 text-sm outline-none"
                                aria-label="Destino de la subida"
                            >
                                <option value={UploadDestination.STANDARD}>{UploadDestination.STANDARD}</option>
                                <option value={UploadDestination.BETA}>{UploadDestination.BETA}</option>
                            </select>
                        </div>
                    )}
                    <div
                        role="button"
                        tabIndex={0}
                        aria-label={t('modals.uploadFileMethod.dragAndDrop')}
                        className={twMerge(
                            'w-full flex flex-col gap-4 justify-center rounded-md items-center border border-dashed bg-[#5417EE0A] dark:bg-[#37474F] border-[#B998FA] dark:border-[#78909C] p-6',
                            isDragginOver &&
                                'border-2 bg-primary/10 dark:bg-primary/20 border-primary/10 dark:border-primary/20',
                        )}
                        onDrop={handleDragAndDrop}
                        onDragEnter={handleDragOver}
                        onDragLeave={handleDragExit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openFileSelector();
                            }
                        }}
                    >
                        <RiUploadCloudFill className="text-[#8048F7] w-12 h-12" />
                        <p className="font-poppins font-medium text-sm">{t('modals.uploadFileMethod.dragAndDrop')}</p>
                        <p className="font-poppins text-xs font-normal text-[#90A4AE]">
                            {t('modals.uploadFileMethod.or')}
                        </p>
                        <Button
                            className="cursor-pointer rounded-md"
                            onClick={openFileSelector}
                            disabled={isLoadingUploadFile}
                        >
                            {t('modals.uploadFileMethod.browse')}
                        </Button>
                        <p className="font-roboto text-xs font-normal text-[#90A4AE]">
                            {isKnowledge
                                ? t('modals.uploadFileMethod.knowledge_formats')
                                : t('modals.uploadFileMethod.formats')}
                        </p>
                    </div>

                    {!disabledByNothingSelected && (
                        <div className="flex flex-col space-y-3 pb-4 pt-3 px-3 border rounded-md max-h-[220px] overflow-y-auto">
                            {/* <div className="mb-4">
                                <Progress value={overallProgress} className="h-2" />
                                <div className="mt-2 text-sm text-muted-foreground">
                                    {t('modals.uploadFile.completed', { progress: overallProgress.toFixed(0) })}
                                </div>
                            </div> */}
                            {/* Import selected list from PC */}
                            {selectedFiles.length > 0 && (
                                <div className="flex flex-col space-y-2">
                                    <span className="text-xs uppercase text-muted-foreground">
                                        Desde el Dispositivo
                                    </span>
                                    {selectedFiles.map((file) => {
                                        const fileKey = `${file.name}|${file.lastModified}`;
                                        const validationError = fileValidationErrors[fileKey];
                                        const showSuccess =
                                            statusUploadFiles?.[file.name] === 'successful' &&
                                            progressByEvents?.[file.name] === 100;
                                        const showError =
                                            (statusUploadFiles?.[file.name] === 'failed' &&
                                                progressByEvents?.[file.name] === 100) ||
                                            !!validationError;
                                        const fileProgress = progressByEvents?.[file.name] || 0;

                                        const rejectedEntry = rejectedUploads?.find(
                                            (error) => error?.reason?._fileName === file.name,
                                        );
                                        const errorByName: string | undefined =
                                            validationError ??
                                            (rejectedEntry ? extractApiErrorMessage(rejectedEntry.reason) : undefined);
                                        return (
                                            <div
                                                key={fileKey}
                                                className="flex flex-col bg-gray-qm-50 dark:bg-gray-qm-800 p-2 rounded-md"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        {(() => {
                                                            const FileTypeIcon = getFileIcon(file.name);
                                                            return <FileTypeIcon className="h-8 w-8 text-blue-500" />;
                                                        })()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-sm font-medium truncate max-w-[400px]">
                                                                {file.name}
                                                            </p>
                                                            <div className="flex items-center gap-2">
                                                                {showSuccess && (
                                                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                                                )}
                                                                {showError && (
                                                                    <XCircle className="h-5 w-5 text-red-500" />
                                                                )}
                                                                {!isUploading && (
                                                                    <button
                                                                        onClick={() =>
                                                                            removeFileFromDevice(
                                                                                `${file.name}|${file.lastModified}`,
                                                                            )
                                                                        }
                                                                        className="group p-1 hover:bg-gray-qm-100 dark:hover:bg-gray-qm-700 rounded-md transition-colors"
                                                                        aria-label="Remover archivo"
                                                                    >
                                                                        <X className="h-4 w-4 text-gray-qm-400 group-hover:text-gray-qm-900 dark:group-hover:text-white" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {fileProgress > 0 && (
                                                            <Progress value={fileProgress} className="h-1 mt-1" />
                                                        )}
                                                    </div>
                                                </div>
                                                {errorByName && (
                                                    <div className="flex flex-col justify-start px-4 py-3 rounded-md border ml-[5px] mt-2 border-[#FCA5A5]">
                                                        {FILE_ERRORS[errorByName] ? (
                                                            <>
                                                                <p className="font-roboto font-semibold text-[12px] leading-3 text-[#FCA5A5] mb-1">
                                                                    {t(`errors.${FILE_ERRORS[errorByName]['title']}`)}
                                                                </p>
                                                                <ReactMarkdown
                                                                    components={{
                                                                        p: ({ node: _node, ...props }) => (
                                                                            <p
                                                                                className="font-roboto font-normal text-[10px] leading-[10px] text-[#FCA5A5]"
                                                                                {...props}
                                                                            />
                                                                        ),
                                                                    }}
                                                                >
                                                                    {t(
                                                                        `errors.${FILE_ERRORS[errorByName]['description']}`,
                                                                    )}
                                                                </ReactMarkdown>
                                                            </>
                                                        ) : (
                                                            <p className="font-roboto font-normal text-[10px] leading-[10px] text-[#FCA5A5]">
                                                                {errorByName}
                                                            </p>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Import selected list from Google */}
                            {selectedFromIntegration.length > 0 && (
                                <div className="flex flex-col space-y-2">
                                    <span className="text-xs uppercase text-muted-foreground">Desde Google Drive</span>
                                    {selectedFromIntegration.map((file) => {
                                        return (
                                            <div
                                                key={file.name}
                                                className="flex flex-col bg-gray-qm-50 dark:bg-gray-qm-800 p-2 rounded-md"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        {(() => {
                                                            const FileTypeIcon = getFileIcon(file.name);
                                                            return <FileTypeIcon className="h-8 w-8 text-blue-500" />;
                                                        })()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <p className="text-sm font-medium truncate max-w-[400px]">
                                                                {file.name}
                                                            </p>
                                                            <div className="flex items-center gap-2">
                                                                {file.fullfiled !== undefined && file.fullfiled && (
                                                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                                                )}
                                                                {file.fullfiled !== undefined && !file.fullfiled && (
                                                                    <XCircle className="h-5 w-5 text-red-500" />
                                                                )}
                                                                {!isUploading && (
                                                                    <button
                                                                        onClick={() =>
                                                                            removeFileFromIntegration(file.id)
                                                                        }
                                                                        className="group p-1 hover:bg-gray-qm-100 dark:hover:bg-gray-qm-700 rounded-md transition-colors"
                                                                        aria-label="Remover archivo"
                                                                    >
                                                                        <X className="h-4 w-4 text-gray-qm-400 group-hover:text-gray-qm-900 dark:group-hover:text-white" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {integrationEventProgres.google[file.name] > 0 && (
                                                            <Progress
                                                                value={integrationEventProgres.google[file.name]}
                                                                className="h-1 mt-1"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                {file?.error && (
                                                    <div className="flex flex-col justify-start px-4 py-3 rounded-md border ml-[5px] mt-2 border-[#FCA5A5]">
                                                        <p className="font-roboto font-semibold text-[12px] leading-3 text-[#FCA5A5] mb-1">
                                                            {file?.error}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Import selected list from URI */}
                            {selectedFilesFromUris.length > 0 && (
                                <div className="flex flex-col space-y-2">
                                    <span className="text-xs uppercase text-muted-foreground">Desde URL</span>
                                    {selectedFilesFromUris.map((file) => {
                                        return (
                                            <div
                                                key={file.uri}
                                                className="flex flex-col bg-gray-qm-50 dark:bg-gray-qm-800 p-2 rounded-md"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="flex-shrink-0">
                                                        {(() => {
                                                            const FileTypeIcon = getFileIcon(file.uri);
                                                            return <FileTypeIcon className="h-8 w-8 text-blue-500" />;
                                                        })()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <p
                                                                className="text-sm font-medium truncate max-w-[400px]"
                                                                title={file.uri}
                                                            >
                                                                {file.uri}
                                                            </p>
                                                            <div className="flex items-center gap-2">
                                                                {file.fullfiled !== undefined && file.fullfiled && (
                                                                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                                                                )}
                                                                {file.fullfiled !== undefined && !file.fullfiled && (
                                                                    <XCircle className="h-5 w-5 text-red-500" />
                                                                )}
                                                                {!isUploading && (
                                                                    <button
                                                                        onClick={() => removeFileFromUri(file.uri)}
                                                                        className="group p-1 hover:bg-gray-qm-100 dark:hover:bg-gray-qm-700 rounded-md transition-colors"
                                                                        aria-label="Remover archivo"
                                                                    >
                                                                        <X className="h-4 w-4 text-gray-qm-400 group-hover:text-gray-qm-900 dark:group-hover:text-white" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {integrationEventProgres.uri[file.uri] > 0 && (
                                                            <Progress
                                                                value={integrationEventProgres.uri[file.uri]}
                                                                className="h-1 mt-1"
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                                {file?.error && (
                                                    <div className="flex flex-col justify-start px-4 py-3 rounded-md border ml-[5px] mt-2 border-[#FCA5A5]">
                                                        <p className="font-roboto font-semibold text-[12px] leading-3 text-[#FCA5A5] mb-1">
                                                            {file?.error}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    <input ref={fileInputRef} type="file" className="hidden" multiple onChange={handleFileSelection} />
                    <div className="flex space-x-4 justify-end">
                        <Button
                            className="rounded-md"
                            variant="ghost"
                            onClick={handleCloseModal}
                            disabled={isUploading}
                        >
                            {t('modals.uploadFileMethod.cancel')}
                        </Button>
                        <Button
                            className="rounded-md"
                            onClick={() => handleUpload()}
                            disabled={isUploading || disabledByNothingSelected || allCompleted || hasValidationErrors}
                        >
                            {isUploading ? (
                                <RiLoader4Line className="text-white animate-spin h-6 text-[24px]" />
                            ) : (
                                t('modals.uploadFileMethod.upload')
                            )}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
            <UriListImport
                isOpen={uriDialogOpen}
                handleClose={(state) => setUriDialogOpen(state)}
                setValidUris={(uris: { uri: string }[]) => setSelectedFilesFromUris((prev) => [...prev, ...uris])}
            />
        </>
    );
};

export default UploadFileMethod;
